<template>
    <div class="v-app-fullscreen-portal">
        <p-toast position="top-center" />

        <portal-app-header v-if="!isFullScreenPage" show-profile />
        <app-menu v-if="!isFullScreenPage" :items="navItems" />
        <loading-spinner />

        <div
            id="app-main-portal"
            class="h-full"
            :class="{
                'p-0': isFullScreenPage,
                'bg-white min-h-screen': !isFullScreenPage
            }"
        >
            <router-view />
        </div>
        <component
            :is="getComponent(currentModal.name)"
            v-bind="currentModal.props"
            @close="hideModal"
        />
        <component
            :is="getComponent(currentConfirmModal.name)"
            v-bind="currentConfirmModal.props"
            @close="hideConfirmModal"
        />
        <inactivity-modal />
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, defineAsyncComponent, ref } from 'vue';
import { useRootStore } from '@/store';
import InactivityModal from '@/components/InactivityModal.vue3.vue';
import AppMenu from '@/components/layout/AppMenu.vue3.vue';
import PortalAppHeader from '@/components/portal/PortalAppHeader.vue3.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue3.vue';
import { useRoute } from 'vue-router';
import { ToastServiceMethods } from 'primevue/toastservice';
import { useToast } from 'primevue/usetoast';
import { MenuItem } from 'primevue/menuitem';

const route = useRoute();
const rootStore = useRootStore();
const currentModal = computed(() => rootStore.state.currentModal);
const currentConfirmModal = computed(() => rootStore.state.currentConfirmModal);
const hideModal = () => rootStore.dispatch('hideModal');
const hideConfirmModal = () => rootStore.dispatch('hideConfirmModal');
const initializeToast = (toast: ToastServiceMethods) => rootStore.dispatch('initializeToast', toast);

const isFullScreenPage = computed(() => {
    return !route.matched.length || !!route.meta?.fullscreen;
});

const modals = {
    ConfirmModal: defineAsyncComponent(() => import('@/components/ConfirmModal.vue3.vue')),
    ResetLoginModal: defineAsyncComponent(() => import('@/components/staff/ResetLoginModal.vue3.vue')),
    ExtendedMessageModal: defineAsyncComponent(() => import('@/components/alerts/ExtendedMessageModal.vue3.vue')),
    InactivityModal: defineAsyncComponent(() => import('@/components/InactivityModal.vue3.vue')),
    SignatureModal: defineAsyncComponent(() => import('@/components/common/SignatureModal.vue3.vue')),
    VisitRequestConfirmationModal: defineAsyncComponent(() => import('@/components/portal/VisitRequestConfirmationModal.vue3.vue')),
    PatientClinicalNotesModal: defineAsyncComponent(() => import('@/components/portal/patient/PatientClinicalNotesModal.vue3.vue')),
    PatientContactSettingsModal: defineAsyncComponent(() => import('@/components/portal/patient/PatientContactSettingsModal.vue3.vue'))
};

const navItems = ref<MenuItem[]>([
    {
        label: 'Home',
        icon: 'pi pi-home', 
        to: '/home',
    },
    {
        label: 'Messages',
        icon: 'pi pi-envelope',
        to: '/messages'
    },
    {
        label: 'Upcoming Appointments',
        icon: 'pi pi-calendar',
        to: '/visits/upcoming'
    },
    {
        label: 'Appointment Requests',
        icon: 'pi pi-clock',
        to: '/visits/requests'
    },
    {
        label: 'Clinical Notes',
        icon: 'pi pi-file',
        to: '/visits/clinical-notes'
    }
]);

function getComponent(name: string) {
    if (!name || !(name in modals)) return;
    return modals[name as keyof typeof modals];
}

onBeforeMount(()=> {
    document.title = "Patient Portal | Net Health";
    initializeToast(useToast());
})

</script>

<style lang="scss">
    @use 'primeflex/primeflex.scss';
    @use '@/styles.scss';
</style>
<style lang="scss" scoped>
    .v-app-fullscreen-portal {
        background-color: #F4F4F4;
    }
    
    @media screen and (max-width: 960px) {
        :deep(#app-main-portal) {
            padding: 48px 0px 56px 0px;
        }
    }
    @media screen and (min-width: 960px) {
        :deep(#app-main-portal) {
            padding: 48px 0px 0px 256px;
        }
    }

    .portal-view {
        background-color: #fefefe;
        padding: 10px;
    }
    
    :deep(.page-content) {
        margin: 0 15px;
    }
</style>
